import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import { Player, PlayerGameStat, PlayerTotals, GameVideo, CoachPortalPost } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { mediaUrl } from '../utils/mediaUrl';
import { parseGameTime } from '../utils/gameTime';

const API_URL  = process.env.REACT_APP_API_URL?.replace('/api', '') ?? 'http://localhost:3001';
const HLS_BASE = process.env.REACT_APP_HLS_URL ?? 'http://localhost:8000';

const fmtDate = (s: string) =>
  parseGameTime(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const fmtTime = (s: string) =>
  parseGameTime(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// ── Stat bar ──────────────────────────────────────────────────────────────────
const StatBar = ({ label, value, max, color = '#F97316', customLabel }: { label: string; value: number; max: number; color?: string; customLabel?: string }) => (
  <div style={{ marginBottom: '12px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
      <span style={{ fontSize: '13px', color: 'var(--ink-3)', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--ink-1)' }}>{customLabel ?? value}</span>
    </div>
    <div style={{ height: '6px', background: 'var(--hairline-strong)', borderRadius: '3px', overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${max > 0 ? Math.min((value / max) * 100, 100) : 0}%`,
        background: color, borderRadius: '3px', transition: 'width 0.6s ease',
      }} />
    </div>
  </div>
);

// ── Highlight clip player ─────────────────────────────────────────────────────
const HighlightModal = ({ clip, onClose }: { clip: GameVideo; onClose: () => void }) => {
  // Build the raw relative/absolute URL using existing convention, then
  // pass through mediaUrl() which makes it absolute when inside Capacitor iOS.
  const rawUrl = clip.file_path.startsWith('http') || clip.file_path.startsWith('/api/')
    ? clip.file_path
    : clip.file_path.startsWith('/uploads/')
      ? `${API_URL}${clip.file_path}`
      : `${HLS_BASE}${clip.file_path}`;
  const videoUrl = mediaUrl(rawUrl);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div style={{ width: '100%', maxWidth: '860px', padding: '0 16px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '16px', color: '#F0F6FC' }}>{clip.title || 'Highlight'}</div>
            {clip.game_title && <div style={{ fontSize: '13px', color: '#8892A4' }}>{clip.game_title}</div>}
          </div>
          <button className="modal-close" onClick={onClose} style={{ color: '#F0F6FC' }}>✕</button>
        </div>
        <video
          src={videoUrl}
          controls
          autoPlay
          playsInline
          style={{ width: '100%', borderRadius: '12px', background: '#000', maxHeight: '70vh' }}
        />
      </div>
    </div>
  );
};

// ── Inline Stat Edit Modal (admin, per game row) ──────────────────────────────
const StatEditModal = ({
  playerId, gameId, gameTitle, sport, initialStats, onClose, onSaved,
}: {
  playerId: string; gameId: string; gameTitle: string; sport: string;
  initialStats: any; onClose: () => void; onSaved: (updatedStats: any) => void;
}) => {
  const isBaseball = sport === 'baseball' || sport === 'softball';
  const [stats, setStats] = useState<any>({ ...initialStats });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const n = (f: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setStats((s: any) => ({ ...s, [f]: Number(e.target.value) }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/players/${playerId}/stats/${gameId}`, stats);
      onSaved(r.data);
      setSaved(true);
      setTimeout(onClose, 900);
    } finally { setSaving(false); }
  };

  const soccerFields: [string, string][] = [
    ['Goals', 'goals'], ['Assists', 'assists'], ['Yellow Cards', 'yellow_cards'],
    ['Red Cards', 'red_cards'], ['Minutes', 'minutes_played'], ['Shots', 'shots'], ['On Target', 'shots_on_target'],
  ];
  const battingFields: [string, string][] = [
    ['AB', 'at_bats'], ['Hits', 'hits'], ['Runs', 'runs'], ['RBI', 'rbis'],
    ['2B', 'doubles'], ['3B', 'triples'], ['HR', 'home_runs'],
    ['BB', 'walks'], ['K', 'strikeouts'], ['SB', 'stolen_bases'],
  ];
  const pitchingFields: [string, string][] = [
    ['IP', 'innings_pitched'], ['ER', 'earned_runs'],
    ['K (p)', 'pitching_strikeouts'], ['BB (p)', 'pitching_walks'], ['Errors', 'errors'],
  ];

  const inp = (label: string, f: string, isIp = false) => (
    <div key={f}>
      <label style={{ fontSize: '10px', color: '#8892A4', display: 'block', marginBottom: '4px', fontWeight: 600 }}>{label}</label>
      <input type="number" min={0} step={isIp ? 0.1 : 1} className="form-control"
        value={stats[f] ?? 0} onChange={n(f)}
        style={{ textAlign: 'center', fontWeight: 800, padding: '6px' }} />
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '16px' }}>Edit Stats — {gameTitle}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {isBaseball ? (
          <>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#8892A4', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>Batting</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '16px' }}>
              {battingFields.map(([l, f]) => inp(l, f))}
            </div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#8892A4', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>Pitching / Fielding</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '16px' }}>
              {pitchingFields.map(([l, f]) => inp(l, f, f === 'innings_pitched'))}
            </div>
          </>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
            {soccerFields.map(([l, f]) => inp(l, f))}
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save Stats'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Add Coach Post Modal ───────────────────────────────────────────────────────
const AddPostModal = ({ playerId, onClose, onCreated }: {
  playerId: string; onClose: () => void; onCreated: (posts: CoachPortalPost[]) => void;
}) => {
  const [title, setTitle]       = useState('');
  const [content, setContent]   = useState('');
  const [clipFile, setClipFile] = useState<File | null>(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (!title.trim() || !content.trim()) { setError('Title and content are required.'); return; }
    setSaving(true);
    setError('');
    try {
      const form = new FormData();
      form.append('title', title.trim());
      form.append('content', content.trim());
      if (clipFile) form.append('clip', clipFile);
      const r = await api.post(`/players/${playerId}/coach-portal`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
      });
      onCreated(r.data);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to post. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <div className="modal-header">
          <h2>Add Coaching Post</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="form-group">
          <label>Title</label>
          <input className="form-control" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. First touch on receiving passes" />
        </div>
        <div className="form-group">
          <label>Coaching Notes</label>
          <textarea
            className="form-control"
            rows={5}
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Describe what you observed and what the player should work on..."
            style={{ resize: 'vertical' }}
          />
        </div>
        <div className="form-group">
          <label>Video Clip <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(optional)</span></label>
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              border: '2px dashed var(--hairline)', borderRadius: '10px', padding: '20px',
              textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s',
              color: clipFile ? 'var(--ink-1)' : 'var(--ink-4)',
            }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = '#F97316')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--hairline)')}
          >
            {clipFile ? `📎 ${clipFile.name}` : '🎬 Click to attach a video clip'}
          </div>
          <input ref={fileRef} type="file" accept="video/*" style={{ display: 'none' }}
            onChange={e => setClipFile(e.target.files?.[0] || null)} />
          {clipFile && (
            <button onClick={() => setClipFile(null)} style={{ marginTop: '6px', background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '12px' }}>
              ✕ Remove clip
            </button>
          )}
        </div>
        {error && <div style={{ color: '#EF4444', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? (clipFile ? '⏳ Uploading…' : '⏳ Posting…') : '📌 Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Coach Portal Post Card ────────────────────────────────────────────────────
const PostCard = ({ post, currentUserId, currentRole, onAck, onReply, onDeletePost, onDeleteReply }: {
  post: CoachPortalPost;
  currentUserId?: string;
  currentRole?: string;
  onAck: (postId: string) => void;
  onReply: (postId: string, content: string) => Promise<void>;
  onDeletePost: (postId: string) => void;
  onDeleteReply: (replyId: string) => void;
}) => {
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying]   = useState(false);
  const [expanded, setExpanded]   = useState(true);
  const isAdmin = currentRole === 'admin';

  const videoUrl = post.video_url
    ? post.video_url.startsWith('/api/') || post.video_url.startsWith('http')
      ? post.video_url
      : null
    : null;

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try { await onReply(post.id, replyText.trim()); setReplyText(''); } finally { setReplying(false); }
  };

  const roleBadge = (role: string) => {
    if (role === 'admin' || role === 'coach') {
      return <span style={{ fontSize: '10px', fontWeight: 800, background: 'rgba(249,115,22,0.15)', color: '#F97316', border: '1px solid rgba(249,115,22,0.3)', borderRadius: '4px', padding: '1px 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Coach</span>;
    }
    return <span style={{ fontSize: '10px', fontWeight: 800, background: 'rgba(96,165,250,0.15)', color: '#60A5FA', border: '1px solid rgba(96,165,250,0.3)', borderRadius: '4px', padding: '1px 6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Player</span>;
  };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '14px', overflow: 'hidden', marginBottom: '16px' }}>
      {/* Post header */}
      <div style={{ padding: '18px 20px 14px', borderBottom: expanded ? '1px solid var(--hairline)' : 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <div style={{ fontWeight: 800, fontSize: '15px', color: 'var(--ink-1)' }}>{post.title}</div>
              {post.acked && (
                <span style={{ fontSize: '10px', fontWeight: 800, background: 'rgba(34,197,94,0.12)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.25)', borderRadius: '4px', padding: '1px 6px' }}>✓ Acknowledged</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--ink-4)' }}>
              {roleBadge('coach')}
              <span>{post.author_username}</span>
              <span>·</span>
              <span>{fmtTime(post.created_at)}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            <button onClick={() => setExpanded(x => !x)} style={{ background: 'none', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: '18px', padding: '0 4px' }}>
              {expanded ? '▲' : '▼'}
            </button>
            {(isAdmin || post.author_id === currentUserId) && (
              <button onClick={() => onDeletePost(post.id)} style={{ background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '14px', opacity: 0.7 }}>✕</button>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '16px 20px' }}>
          {/* Content */}
          <p style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--ink-2)', whiteSpace: 'pre-wrap', margin: '0 0 16px' }}>{post.content}</p>

          {/* Video clip */}
          {videoUrl && (
            <div style={{ marginBottom: '16px' }}>
              <video
                src={videoUrl}
                controls
                playsInline
                style={{ width: '100%', borderRadius: '10px', background: '#000', maxHeight: '360px' }}
              />
            </div>
          )}

          {/* Acknowledge button */}
          {currentUserId && (
            <div style={{ marginBottom: '16px' }}>
              {post.acked ? (
                <button
                  onClick={() => onAck(post.id)}
                  style={{ fontSize: '13px', fontWeight: 700, padding: '7px 16px', borderRadius: '8px', cursor: 'pointer', background: 'rgba(34,197,94,0.12)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.3)' }}
                >
                  ✓ Acknowledged — click to undo
                </button>
              ) : (
                <button
                  onClick={() => onAck(post.id)}
                  style={{ fontSize: '13px', fontWeight: 700, padding: '7px 16px', borderRadius: '8px', cursor: 'pointer', background: 'rgba(255,255,255,0.05)', color: 'var(--ink-3)', border: '1px solid var(--hairline)' }}
                >
                  ☐ Mark as Acknowledged
                </button>
              )}
              <span style={{ fontSize: '12px', color: 'var(--ink-4)', marginLeft: '10px' }}>
                {post.acked_by.length > 0 ? `${post.acked_by.length} acknowledgment${post.acked_by.length > 1 ? 's' : ''}` : 'No acknowledgments yet'}
              </span>
            </div>
          )}

          {/* Replies */}
          {post.replies.length > 0 && (
            <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: '14px', marginBottom: '14px' }}>
              {post.replies.map(reply => (
                <div key={reply.id} style={{
                  display: 'flex', gap: '10px', marginBottom: '12px',
                }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: (reply.author_role === 'admin' || reply.author_role === 'coach') ? 'rgba(249,115,22,0.2)' : 'rgba(96,165,250,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: 800,
                    color: (reply.author_role === 'admin' || reply.author_role === 'coach') ? '#F97316' : '#60A5FA',
                  }}>
                    {reply.author_username.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink-1)' }}>{reply.author_username}</span>
                      {roleBadge(reply.author_role)}
                      <span style={{ fontSize: '11px', color: 'var(--ink-4)' }}>{fmtTime(reply.created_at)}</span>
                      {(isAdmin || reply.author_id === currentUserId) && (
                        <button onClick={() => onDeleteReply(reply.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#EF4444', cursor: 'pointer', fontSize: '12px', opacity: 0.6 }}>✕</button>
                      )}
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--ink-2)', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>{reply.content}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Reply composer */}
          {currentUserId && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <textarea
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Add a comment or question…"
                rows={2}
                style={{ flex: 1, background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', borderRadius: '8px', color: 'var(--ink-1)', padding: '8px 12px', fontSize: '13px', resize: 'none', fontFamily: 'inherit' }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitReply(); } }}
              />
              <button
                onClick={submitReply}
                disabled={replying || !replyText.trim()}
                style={{ padding: '8px 14px', borderRadius: '8px', background: '#F97316', border: 'none', color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' }}
              >
                {replying ? '…' : 'Reply'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const PlayerProfile = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const { user, isAdmin, isCoach, isPlayerParent, linkedPlayerId, isTeamManager, managedTeamIds } = useAuth();

  const [player, setPlayer]         = useState<Player | null>(null);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState<'stats' | 'highlights' | 'coaching'>('stats');
  const [watchingClip, setWatchingClip] = useState<GameVideo | null>(null);
  const [editingStat, setEditingStat]   = useState<{ gameId: string; gameTitle: string; stats: any } | null>(null);

  // Coaching tab state
  const [posts, setPosts]           = useState<CoachPortalPost[]>([]);
  const [canPost, setCanPost]       = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  const [showAddPost, setShowAddPost]   = useState(false);

  // Pending player/parent approvals
  const [pendingParents, setPendingParents] = useState<any[]>([]);

  const refreshPlayer = useCallback(() => {
    if (!playerId) return;
    api.get(`/players/${playerId}`).then(r => setPlayer(r.data)).catch(() => {});
  }, [playerId]);

  useEffect(() => {
    if (!playerId) return;
    api.get(`/players/${playerId}`)
      .then(r => { setPlayer(r.data); fetchPendingParents(r.data); })
      .finally(() => setLoading(false));
  }, [playerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPosts = useCallback(async () => {
    if (!playerId) return;
    setPostsLoading(true);
    try {
      const r = await api.get(`/players/${playerId}/coach-portal`);
      setPosts(r.data.posts);
      setCanPost(r.data.can_post);
    } catch {} finally { setPostsLoading(false); }
  }, [playerId]);

  // Which users can approve pending player/parents for this player
  const canApprovePeers = (p: typeof player) =>
    isAdmin ||
    (isTeamManager && p?.team_id != null && managedTeamIds.includes(p.team_id)) ||
    (isPlayerParent && linkedPlayerId === playerId);

  const fetchPendingParents = useCallback(async (p: typeof player) => {
    if (!playerId || !canApprovePeers(p)) return;
    try {
      const r = await api.get(`/players/${playerId}/pending-parents`);
      setPendingParents(r.data);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId, isAdmin, isTeamManager, isPlayerParent, linkedPlayerId, managedTeamIds]);

  const handleApproveParent = async (userId: string) => {
    try {
      await api.post(`/players/${playerId}/approve-parent/${userId}`);
      setPendingParents(prev => prev.filter(u => u.id !== userId));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleRejectParent = async (userId: string) => {
    if (!window.confirm('Reject this Player/Parent request?')) return;
    try {
      await api.post(`/players/${playerId}/reject-parent/${userId}`);
      setPendingParents(prev => prev.filter(u => u.id !== userId));
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to reject');
    }
  };

  // Which users can see the Coaching tab for this player
  const canViewCoachingTab =
    isAdmin ||
    isCoach ||
    (isPlayerParent && linkedPlayerId === playerId);

  // Auto-redirect away from coaching tab if user can't view it
  useEffect(() => {
    if (activeTab === 'coaching' && !canViewCoachingTab) setActiveTab('stats');
  }, [activeTab, canViewCoachingTab]);

  useEffect(() => {
    if (activeTab === 'coaching' && canViewCoachingTab) fetchPosts();
  }, [activeTab, fetchPosts, canViewCoachingTab]);

  const handleAck = async (postId: string) => {
    try {
      const r = await api.post(`/coach-portal/posts/${postId}/ack`);
      setPosts(prev => prev.map(p => {
        if (p.id !== postId) return p;
        const acked_by = r.data.acknowledged
          ? [...p.acked_by, user!.id]
          : p.acked_by.filter(id => id !== user!.id);
        return { ...p, acked_by, acked: r.data.acknowledged };
      }));
    } catch {}
  };

  const handleReply = async (postId: string, content: string) => {
    const r = await api.post(`/coach-portal/posts/${postId}/replies`, { content });
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, replies: [...p.replies, r.data] } : p));
  };

  const handleDeletePost = async (postId: string) => {
    if (!window.confirm('Delete this coaching post?')) return;
    await api.delete(`/coach-portal/posts/${postId}`);
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  const handleDeleteReply = async (replyId: string) => {
    if (!window.confirm('Delete this reply?')) return;
    await api.delete(`/coach-portal/replies/${replyId}`);
    setPosts(prev => prev.map(p => ({ ...p, replies: p.replies.filter(r => r.id !== replyId) })));
  };

  if (loading) return <div className="container" style={{ textAlign: 'center', padding: '80px', color: 'var(--ink-4)' }}>Loading...</div>;
  if (!player) return <div className="container" style={{ textAlign: 'center', padding: '80px', color: '#EF4444' }}>Player not found.</div>;

  const totals: PlayerTotals = player.totals || { goals: 0, assists: 0, yellow_cards: 0, red_cards: 0, minutes_played: 0, shots: 0, shots_on_target: 0, at_bats: 0, hits: 0, runs: 0, rbis: 0, doubles: 0, triples: 0, home_runs: 0, walks: 0, strikeouts: 0, stolen_bases: 0, innings_pitched: 0, earned_runs: 0, pitching_strikeouts: 0, pitching_walks: 0, errors: 0, games_played: 0 };
  const sport = player.team?.sport || 'soccer';
  const isBaseball   = sport === 'baseball' || sport === 'softball';
  const isBasketball = sport === 'basketball';
  const battingAvg = totals.at_bats > 0 ? (totals.hits / totals.at_bats).toFixed(3).replace(/^0/, '') : '.000';
  const era = totals.innings_pitched > 0 ? ((totals.earned_runs / totals.innings_pitched) * 9).toFixed(2) : '0.00';
  const gameStats: PlayerGameStat[] = player.gameStats || [];
  const highlights: GameVideo[] = player.highlights || [];
  const teamColor = player.team?.primary_color || '#F97316';
  const photoSrc = player.photo_url ? mediaUrl(player.photo_url) : null;

  const Tab = ({ id, label }: { id: typeof activeTab; label: string }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: '8px 16px', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '14px',
        cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0,
        minHeight: '44px', touchAction: 'manipulation', WebkitAppearance: 'none',
        background: activeTab === id ? 'rgba(249,115,22,0.15)' : 'transparent',
        color: activeTab === id ? '#F97316' : 'var(--ink-3)',
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="container">
      {/* Back link */}
      {player.team ? (
        <Link to={`/teams/${player.team_id}`} style={{ fontSize: '13px', color: 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '20px' }}>
          ← {player.team.name}
        </Link>
      ) : (
        <Link to="/teams" style={{ fontSize: '13px', color: 'var(--ink-3)', display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '20px' }}>
          ← Teams
        </Link>
      )}

      {/* Profile header */}
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px',
        padding: '28px', marginBottom: '24px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '4px',
          background: teamColor,
        }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
          {/* Photo */}
          <div style={{
            width: 80, height: 80, borderRadius: '50%', flexShrink: 0,
            background: photoSrc ? 'transparent' : teamColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '28px', fontWeight: 900, color: '#fff', overflow: 'hidden',
            border: '3px solid var(--hairline)',
          }}>
            {photoSrc
              ? <img src={photoSrc} alt={player.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : `#${player.number ?? '?'}`}
          </div>

          {/* Info */}
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '26px', fontWeight: 900, letterSpacing: '-0.3px', marginBottom: '4px' }}>{player.name}</h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
              {player.number != null && (
                <span style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 10px', fontSize: '13px', fontWeight: 700, color: 'var(--ink-1)' }}>
                  #{player.number}
                </span>
              )}
              {player.position && (
                <span style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 10px', fontSize: '13px', color: 'var(--ink-3)' }}>
                  {player.position}
                </span>
              )}
              {player.grade && (
                <span style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 10px', fontSize: '13px', color: 'var(--ink-3)' }}>
                  {player.grade}
                </span>
              )}
              {player.age && (
                <span style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '6px', padding: '3px 10px', fontSize: '13px', color: 'var(--ink-3)' }}>
                  Age {player.age}
                </span>
              )}
            </div>
            {player.team && (
              <Link to={`/teams/${player.team_id}`} style={{ fontSize: '13px', color: '#F97316', fontWeight: 600 }}>
                {player.team.name} · {player.team.season_year}
              </Link>
            )}
          </div>

          {/* Season summary */}
          <div style={{ display: 'flex', gap: '20px', flexShrink: 0 }}>
            {(isBaseball ? [
              { label: 'AVG', value: battingAvg,          color: '#22C55E' },
              { label: 'RBI', value: totals.rbis,         color: '#F97316' },
              { label: 'HR',  value: totals.home_runs,    color: '#EF4444' },
            ] : [
              { label: 'Goals',  value: totals.goals,        color: '#22C55E' },
              { label: 'Assists',value: totals.assists,      color: '#F97316' },
            ]).map(({ label, value, color }) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '28px', fontWeight: 900, color, lineHeight: 1 }}>{value ?? 0}</div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pending Player/Parent approvals (visible to linked parents, TMs, admins) */}
      {canApprovePeers(player) && pendingParents.length > 0 && (
        <div style={{
          background: 'rgba(168,85,247,0.05)',
          border: '1px solid rgba(168,85,247,0.2)',
          borderRadius: '12px', padding: '16px 20px', marginBottom: '20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{ fontSize: '15px', fontWeight: 800, color: '#A78BFA' }}>
              ⏳ Pending Player/Parent Requests
            </span>
            <span style={{
              background: 'rgba(168,85,247,0.15)', color: '#A78BFA',
              border: '1px solid rgba(168,85,247,0.3)',
              padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700,
            }}>
              {pendingParents.length}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pendingParents.map((u: any) => (
              <div key={u.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
                background: 'var(--surface)', border: '1px solid var(--hairline)',
                borderRadius: '8px', padding: '10px 14px',
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, #A78BFA, #7C3AED)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: 800, color: '#fff',
                }}>
                  {u.username.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: '120px' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px' }}>{u.username}</div>
                  <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>{u.email}</div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button
                    className="btn btn-sm"
                    style={{
                      background: 'rgba(168,85,247,0.12)', color: '#A78BFA',
                      border: '1px solid rgba(168,85,247,0.3)', fontWeight: 700,
                    }}
                    onClick={() => handleApproveParent(u.id)}
                  >
                    ✓ Approve
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRejectParent(u.id)}
                  >
                    ✕ Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tab-bar">
        <Tab id="stats"      label="Stats" />
        <Tab id="highlights" label={`Highlights (${highlights.length})`} />
        {canViewCoachingTab && <Tab id="coaching" label="🎯 Coach Portal" />}
      </div>

      {/* ── Stats Tab ── */}
      {activeTab === 'stats' && (
        <div className="player-stats-grid">
          {/* Season totals */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '20px' }}>
            <div className="section-label" style={{ marginBottom: '16px' }}>Season Totals</div>

            {isBaseball ? (
              <>
                {/* Batting */}
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>Batting</div>
                <StatBar label="Batting Avg" value={parseFloat(battingAvg) * 1000 | 0} max={1000} color="#22C55E" customLabel={battingAvg} />
                <StatBar label="Hits"        value={totals.hits}       max={Math.max(totals.hits, 10)}       color="#F97316" />
                <StatBar label="RBI"         value={totals.rbis}       max={Math.max(totals.rbis, 10)}       color="#F97316" />
                <StatBar label="Home Runs"   value={totals.home_runs}  max={Math.max(totals.home_runs, 5)}   color="#EF4444" />
                <hr className="divider" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>
                  {[
                    ['AB', totals.at_bats], ['R', totals.runs], ['2B', totals.doubles],
                    ['3B', totals.triples], ['BB', totals.walks], ['K', totals.strikeouts],
                    ['SB', totals.stolen_bases],
                  ].map(([label, val]) => (
                    <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--ink-3)' }}>{label}</span>
                      <span style={{ fontWeight: 700 }}>{val as number}</span>
                    </div>
                  ))}
                </div>
                {(totals.innings_pitched > 0) && (
                  <>
                    <hr className="divider" />
                    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>Pitching</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>
                      {[
                        ['IP', totals.innings_pitched.toFixed(1)], ['ERA', era],
                        ['K', totals.pitching_strikeouts], ['BB', totals.pitching_walks],
                        ['ER', totals.earned_runs],
                      ].map(([label, val]) => (
                        <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: 'var(--ink-3)' }}>{label}</span>
                          <span style={{ fontWeight: 700 }}>{val as string | number}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {totals.errors > 0 && (
                  <>
                    <hr className="divider" />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                      <span style={{ color: 'var(--ink-3)' }}>Errors</span>
                      <span style={{ fontWeight: 700, color: '#EF4444' }}>{totals.errors}</span>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <StatBar label="Goals"   value={totals.goals}   max={Math.max(totals.goals, 10)}   color="#22C55E" />
                <StatBar label="Assists" value={totals.assists} max={Math.max(totals.assists, 10)} color="#F97316" />
                <hr className="divider" />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '8px' }}>
                  <span style={{ color: '#EAB308' }}>🟨 Yellow Cards</span>
                  <span style={{ fontWeight: 800 }}>{totals.yellow_cards}</span>
                </div>
                {totals.red_cards > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginTop: '8px' }}>
                    <span style={{ color: '#EF4444' }}>🟥 Red Cards</span>
                    <span style={{ fontWeight: 800 }}>{totals.red_cards}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Per-game table */}
          <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--hairline)' }}>
              <div className="section-label">Game Log</div>
            </div>
            {!gameStats.length ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--ink-4)' }}>No game stats recorded yet.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
                      {(isBaseball
                        ? ['Date', 'Opponent', 'AB', 'H', 'R', 'RBI', 'HR', 'BB', 'K', 'SB']
                        : ['Date', 'Opponent', 'G', 'A', 'YC', 'RC']
                      ).map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: h === 'Date' || h === 'Opponent' ? 'left' : 'center', color: 'var(--ink-4)', fontWeight: 700, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                      {isAdmin && <th style={{ padding: '10px 8px', width: '40px' }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {gameStats.map((stat, i) => (
                      <tr key={stat.id} style={{ borderBottom: i < gameStats.length - 1 ? '1px solid var(--hairline)' : 'none', background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                        <td style={{ padding: '10px 14px', color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{fmtDate(stat.scheduled_at)}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--ink-1)' }}>
                          vs. {stat.away_team === stat.home_team ? stat.home_team : stat.away_team}
                          {stat.home_final_score != null && stat.away_final_score != null
                            ? <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}> ({stat.home_final_score}–{stat.away_final_score})</span>
                            : null}
                        </td>
                        {(isBaseball
                          ? [stat.at_bats, stat.hits, stat.runs, stat.rbis, stat.home_runs, stat.walks, stat.strikeouts, stat.stolen_bases]
                          : [stat.goals, stat.assists, stat.yellow_cards, stat.red_cards]
                        ).map((v, j) => (
                          <td key={j} style={{ padding: '10px 14px', textAlign: 'center', fontWeight: v > 0 ? 700 : 400, color: v > 0 ? 'var(--ink-1)' : 'var(--ink-4)' }}>{v}</td>
                        ))}
                        {isAdmin && !isBasketball && (
                          <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                            <button
                              title="Edit stats"
                              onClick={() => setEditingStat({ gameId: stat.game_id, gameTitle: stat.title || `${stat.home_team} vs ${stat.away_team}`, stats: stat })}
                              style={{ background: 'none', border: '1px solid var(--hairline)', borderRadius: '5px', color: 'var(--ink-3)', cursor: 'pointer', fontSize: '13px', padding: '3px 7px', lineHeight: 1 }}
                            >✏️</button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Highlights Tab ── */}
      {activeTab === 'highlights' && (
        <div>
          {!highlights.length ? (
            <div style={{
              textAlign: 'center', padding: '80px 20px',
              background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎬</div>
              <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>No highlights yet</h3>
              <p style={{ color: 'var(--ink-3)', fontSize: '14px' }}>Highlight clips can be uploaded from the Admin panel.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' }}>
              {highlights.map(clip => (
                <div
                  key={clip.id}
                  onClick={() => setWatchingClip(clip)}
                  style={{
                    background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px',
                    overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.15s, transform 0.1s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--hairline-strong)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--hairline)'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                >
                  <div style={{ height: '130px', background: 'var(--surface-sunken)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {clip.thumbnail_url ? (
                      <img src={mediaUrl(clip.thumbnail_url)} alt={clip.title || 'Highlight'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ fontSize: '36px', opacity: 0.3 }}>⚽</div>
                    )}
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(249,115,22,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><polygon points="7,4 17,10 7,16" fill="white"/></svg>
                      </div>
                    </div>
                  </div>
                  <div style={{ padding: '12px' }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--ink-1)', marginBottom: '4px' }}>{clip.title || 'Highlight Clip'}</div>
                    {clip.game_title && <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>{clip.game_title}</div>}
                    {clip.scheduled_at && <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '2px' }}>{fmtDate(clip.scheduled_at)}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Coach Portal Tab ── */}
      {activeTab === 'coaching' && canViewCoachingTab && (
        <div>
          {/* Header row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ fontSize: '13px', color: 'var(--ink-3)' }}>
              {posts.length} coaching post{posts.length !== 1 ? 's' : ''} for {player.name}
            </div>
            {canPost && (
              <button className="btn btn-primary" onClick={() => setShowAddPost(true)}>
                ＋ Add Post
              </button>
            )}
          </div>

          {postsLoading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>Loading…</div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 20px', background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎯</div>
              <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>No coaching posts yet</h3>
              <p style={{ color: 'var(--ink-3)', fontSize: '14px' }}>
                {canPost ? 'Click "+ Add Post" to share feedback with this player.' : "Your coach hasn't posted any feedback here yet."}
              </p>
            </div>
          ) : (
            posts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                currentUserId={user?.id}
                currentRole={user?.role}
                onAck={handleAck}
                onReply={handleReply}
                onDeletePost={handleDeletePost}
                onDeleteReply={handleDeleteReply}
              />
            ))
          )}
        </div>
      )}

      {/* Modals */}
      {watchingClip && <HighlightModal clip={watchingClip} onClose={() => setWatchingClip(null)} />}
      {showAddPost && playerId && (
        <AddPostModal
          playerId={playerId}
          onClose={() => setShowAddPost(false)}
          onCreated={(newPosts) => setPosts(newPosts)}
        />
      )}
      {editingStat && playerId && (
        <StatEditModal
          playerId={playerId}
          gameId={editingStat.gameId}
          gameTitle={editingStat.gameTitle}
          sport={sport}
          initialStats={editingStat.stats}
          onClose={() => setEditingStat(null)}
          onSaved={() => { setEditingStat(null); refreshPlayer(); }}
        />
      )}
    </div>
  );
};

export default PlayerProfile;
